///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2026
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { format } from "date-fns";
import { cloneDeep, orderBy, uniq, sortBy } from "lodash";

import { dateFormat, laneType, order } from "../helpers/types";
import filterPricingLanes from "./filterPricingLanes";
import getPricingGridColumns from "./getPricingGridColumns";
import messages from "./messages";
import normalizeColumnFilterItemsForPersistence from "./normalizeColumnFilterItemsForPersistence";
import { coerceFacetValue } from "./rxoTlFacetFormat";
import tlColumnConstants from "./tlColumnConstants";

const { LANE_HAS_MATCHES } = tlColumnConstants;

const LARGE_RXO_TL_FACET_SORT_THRESHOLD = 100;

/** RXO TL server facets return "true"/"false" strings; map labels to Checked / Unchecked. */
const RXO_TL_BOOLEAN_FACET_FIELDS = new Set([
  "hazmat",
  "originDropTrailer",
  "destinationDropTrailer",
  "permit",
  "team",
  "hrhv",
  "roundTrip",
  "multiStop"
]);

export const pricingSliceHelper = {
  requestPricingLanes: (
    state,
    { payload: { clearFilterAndSortDetails, division, isCoyoteBid } },
    initialState
  ) => {
    if (clearFilterAndSortDetails) {
      state.lanes = initialState.lanes;
      state.totalLanes = initialState.totalLanes;
      state.averageMargin = initialState.averageMargin;
      state.totalFiltered = initialState.totalFiltered;
      state.columnFilters = initialState.columnFilters;
      state.rangeFilters = initialState.rangeFilters;
      state.sortField = initialState.sortField;
      state.sortOrder = initialState.sortOrder;
      state.searchTerm = initialState.searchTerm;
      state.perPage = initialState.perPage;
      state.offset = initialState.offset;
      state.lanesLoading = true;
      state.lanesError = initialState.lanesError;
      state.columnFilterMenu = initialState.columnFilterMenu;
      state.filtersApplied = initialState.filtersApplied;
      state.rxoPricingFilterFacets = initialState.rxoPricingFilterFacets;
      state.useRxoTlServerPagedLanes = false;
    } else {
      state.lanes = initialState.lanes;
      state.totalLanes = initialState.totalLanes;
      state.averageMargin = initialState.averageMargin;
      state.totalFiltered = initialState.totalFiltered;
      state.lanesLoading = true;
      state.lanesError = initialState.lanesError;
      state.columnFilterMenu = initialState.columnFilterMenu;
      state.filtersApplied = initialState.filtersApplied;
    }
    state.saveLaneEditsFailure = false;
    state.saveLaneEditsLoading = false;
    if (division) {
      state.division = division;
    }
    if (isCoyoteBid !== undefined) {
      state.isCoyoteBid = isCoyoteBid;
    }
  },
  receivePricingLanesSuccess: (
    state,
    {
      payload: {
        lanesResponse: { lanes, totalLanes, averageMargin },
        requestedLaneType
      }
    },
    initialState
  ) => {
    state.lanes = lanes.map((lane, index) => ({
      ...lane,
      metaData: {
        matched: true,
        defaultIndex: index,
        originaLane:
          requestedLaneType === laneType.standardTruckload ? lane : undefined,
        change:
          requestedLaneType === laneType.standardTruckload ? {} : undefined,
        changedByUserValue:
          requestedLaneType === laneType.standardTruckload ? {} : undefined
      }
    }));
    state.totalLanes = totalLanes;
    state.averageMargin = averageMargin;
    state.totalFiltered = totalLanes;
    state.syncOn = format(new Date(), dateFormat.hmma);
    state.lanesLoading = initialState.lanesLoading;
    state.lanesError = initialState.lanesError;
    state.columnFilterMenu = initialState.columnFilterMenu;
    state.filtersApplied = initialState.filtersApplied;
    state.useRxoTlServerPagedLanes = false;
  },
  receiveRxoTlPagedLanesSuccess: (
    state,
    {
      payload: {
        lanesResponse: { lanes, totalCount, averageMargin },
        requestedLaneType
      }
    },
    initialState
  ) => {
    state.lanes = (lanes || []).map((lane, index) => ({
      ...lane,
      metaData: {
        matched: true,
        defaultIndex: index,
        originaLane:
          requestedLaneType === laneType.standardTruckload ? lane : undefined,
        change:
          requestedLaneType === laneType.standardTruckload ? {} : undefined,
        changedByUserValue:
          requestedLaneType === laneType.standardTruckload ? {} : undefined
      }
    }));
    state.totalLanes = totalCount;
    state.averageMargin = averageMargin;
    state.totalFiltered = totalCount;
    state.syncOn = format(new Date(), dateFormat.hmma);
    state.lanesLoading = initialState.lanesLoading;
    state.lanesError = initialState.lanesError;
    state.columnFilterMenu = initialState.columnFilterMenu;
    state.useRxoTlServerPagedLanes = true;
  },
  requestRxoTlPricingSearchStart: state => {
    state.lanesLoading = true;
    state.lanesError = false;
  },
  receivePricingLanesFailure: state => {
    state.lanes = null;
    state.lanesLoading = false;
    state.lanesError = true;
    state.useRxoTlServerPagedLanes = false;
  },
  updateServerPagedPricingFilters: (state, { payload }) => {
    if (payload.searchTerm !== undefined) {
      state.searchTerm = payload.searchTerm;
    }
    if (payload.items && payload.field) {
      state.columnFilters[payload.field] =
        normalizeColumnFilterItemsForPersistence(payload.items);
    }
    if (payload.range && payload.field) {
      state.rangeFilters[payload.field] = payload.range;
    }
    state.offset = 0;
    let applied = Boolean(
      state.searchTerm && String(state.searchTerm).trim() !== ""
    );
    if (!applied) {
      for (const k of Object.keys(state.columnFilters)) {
        if (state.columnFilters[k]?.some(x => x.selected)) {
          applied = true;
          break;
        }
      }
    }
    if (!applied) {
      for (const k of Object.keys(state.rangeFilters)) {
        const r = state.rangeFilters[k];
        if (
          r &&
          (String(r.minValue) !== "" || String(r.maxValue) !== "")
        ) {
          applied = true;
          break;
        }
      }
    }
    state.filtersApplied = applied;
  },
  resetRxoTlPricingGridFilters: (state, initialState) => {
    state.filtersApplied = false;
    state.columnFilters = initialState.columnFilters;
    state.rangeFilters = initialState.rangeFilters;
    state.columnFilterMenu = initialState.columnFilterMenu;
    state.searchTerm = "";
    state.offset = 0;
  },
  loadColumns: ({ columns }, defaultOrder, storageOrder, forceLoad = false) => {
    if (
      columns.order.length === 0 ||
      forceLoad ||
      columns.order.length !== defaultOrder.length
    ) {
      try {
        storageOrder = JSON.parse(storageOrder);
      } catch {
        storageOrder = null;
      }
      if (
        storageOrder?.length <= defaultOrder.length &&
        storageOrder.every(d => defaultOrder.indexOf(d) !== -1)
      ) {
        columns.order = storageOrder;
        columns.changed =
          defaultOrder.length !== storageOrder.length ||
          defaultOrder.some((c, index) => c !== storageOrder[index]);
      } else {
        columns.order = defaultOrder;
        columns.changed = false;
      }
    }
  },
  setColumnOrder: ({ columns }, { payload: { pick, drop } }, defaultOrder) => {
    let columnOrder = columns.order;
    const index = columnOrder.indexOf(drop);
    if (index !== -1) {
      columnOrder = columnOrder.filter(c => c !== pick);
      columnOrder.splice(index, 0, pick);
      columns.order = columnOrder;
      columns.changed = defaultOrder.some(
        (c, index) => c !== columnOrder[index]
      );
    }
  },
  toggleColumnFilterMenu: (
    state,
    {
      payload: {
        field: columnField,
        visible,
        role,
        filter,
        analyticsGridView,
        filterField
      }
    }
  ) => {
    state.columnFilterMenu = { field: columnField, visible };
    const field = filterField ?? columnField;

    const columnFilters = state.columnFilters[field];
    if (filter) {
      if (visible) {
        const stateCopy = cloneDeep(state);
        if (stateCopy.columnFilters[field]) {
          stateCopy.columnFilters[field] = [];
        }
        filterPricingLanes(stateCopy, {
          payload: {
            applyOnExistingFilters: true,
            role,
            analyticsGridView
          }
        });
        const { format } = getPricingGridColumns(
          role,
          state.division,
          analyticsGridView,
          state.isCoyoteBid
        ).find(f => f.field === field);
        let filterOptions = uniq(
          stateCopy.lanes
            .filter(l => l.metaData.matched)
            .map(l => (format ? format(l[field]) : l[field]))
        );

        if (field === LANE_HAS_MATCHES) {
          filterOptions = filterOptions.filter(
            v => v !== undefined && v !== null && v !== ""
          );
        }

        filterOptions = filterOptions.map((v, i) => ({
          key: i + 1,
          label:
            v === undefined || v === null
              ? ""
              : v === true
              ? messages.checked.defaultMessage
              : v === false
              ? messages.unchecked.defaultMessage
              : v,
          value: v
        }));

        state.columnFilters[field] = sortBy(
          filterOptions.map(f => {
            const option =
              columnFilters && columnFilters.find(o => o.value === f.value);
            return { ...f, selected: option && option.selected };
          }),
          f => {
            return f.label.toString().toLowerCase();
          }
        );
      } else {
        state.columnFilters[field] =
          columnFilters && columnFilters.filter
            ? columnFilters.filter(f => f.selected)
            : [];
      }
    }
  },
  toggleRxoTlColumnFilterMenu: (
    state,
    {
      payload: {
        field: columnField,
        visible,
        role,
        filter,
        analyticsGridView,
        filterField
      }
    }
  ) => {
    state.columnFilterMenu = { field: columnField, visible };
    const field = filterField ?? columnField;

    const columnFilters = state.columnFilters[field];
    if (filter) {
      if (visible) {
        const facetValues = state.rxoPricingFilterFacets?.[field] ?? [];
        const { format } = getPricingGridColumns(
          role,
          state.division,
          analyticsGridView,
          state.isCoyoteBid
        ).find(f => f.field === field);
        const extraSelected = (columnFilters || [])
          .filter(o => o.selected)
          .map(o => o.value)
          .filter(
            v =>
              v !== undefined &&
              !facetValues.some(fv => String(fv) === String(v))
          );
        const rawValues = uniq([...facetValues, ...extraSelected]);

        const filterOptions = rawValues.map((v, i) => {
          let label;
          const isBoolFacetField = RXO_TL_BOOLEAN_FACET_FIELDS.has(field);
          const strBool =
            typeof v === "string" ? v.trim().toLowerCase() : null;
          if (
            isBoolFacetField &&
            (strBool === "true" || strBool === "false")
          ) {
            label =
              strBool === "true"
                ? messages.checked.defaultMessage
                : messages.unchecked.defaultMessage;
          } else if (v === undefined || v === null) {
            label = "";
          } else if (v === "") {
            label = "";
          } else if (v === true) {
            label = messages.checked.defaultMessage;
          } else if (v === false) {
            label = messages.unchecked.defaultMessage;
          } else {
            const coerced = coerceFacetValue(field, v);
            label = format ? format(coerced) : v;
          }
          if (label === undefined || label === null) {
            label = v === undefined || v === null ? "" : String(v);
          }
          return {
            key: i + 1,
            label,
            value: v
          };
        });

        const withSelection = filterOptions.map(f => {
          const option =
            columnFilters && columnFilters.find(o => o.value === f.value);
          return { ...f, selected: option && option.selected };
        });
        state.columnFilters[field] =
          rawValues.length > LARGE_RXO_TL_FACET_SORT_THRESHOLD
            ? withSelection
            : sortBy(
                withSelection,
                f => (f.label ?? "").toString().toLowerCase()
              );
      } else {
        state.columnFilters[field] =
          columnFilters && columnFilters.filter
            ? columnFilters.filter(f => f.selected)
            : [];
      }
    }
  },
  filterPricingLanes: filterPricingLanes,
  setColumnWidth: (state, { payload: { width, field } }) => {
    state.columnWidths[field] = width;
  },
  setSortStateOnly: (state, { payload: { field } }) => {
    const defaultIndex = "metaData.defaultIndex";
    if (field) {
      if (state.sortOrder === order.descending) {
        state.sortOrder = order.ascending;
        if (state.sortField === field) {
          state.sortField = defaultIndex;
        } else {
          state.sortField = field;
        }
      } else if (state.sortField === field) {
        state.sortOrder = order.descending;
      } else {
        state.sortOrder = order.ascending;
        state.sortField = field;
      }
    }
    if (
      field ||
      state.totalFiltered <= state.offset
    ) {
      state.offset = 0;
    }
  },
  sortPricingLanes: (state, { payload: { field } }) => {
    const defaultIndex = "metaData.defaultIndex";
    if (field) {
      if (state.sortOrder === order.descending) {
        state.sortOrder = order.ascending;
        if (state.sortField === field) {
          state.sortField = defaultIndex;
        } else {
          state.sortField = field;
        }
      } else if (state.sortField === field) {
        state.sortOrder = order.descending;
      } else {
        state.sortOrder = order.ascending;
        state.sortField = field;
      }
    }
    const { sortField, sortOrder } = state;
    state.lanes = orderBy(
      state.lanes,
      [
        lane => {
          if (defaultIndex === sortField) {
            return 0;
          } else {
            const value = lane[sortField];
            if (value && value.match && value.match(/^[1-9]\d*(\.\d+)?$/)) {
              return 1;
            } else {
              return 2;
            }
          }
        },
        lane => {
          const value =
            sortField === defaultIndex
              ? lane.metaData.defaultIndex
              : lane[sortField];
          if (!value || typeof value !== "string") {
            return value;
          } else if (
            lane[sortField].match &&
            lane[sortField].match(/^[1-9]\d*(\.\d+)?$/)
          ) {
            return parseFloat(value);
          } else {
            return lane[sortField].toLowerCase();
          }
        }
      ],
      [sortOrder, sortOrder]
    );
    if (
      field ||
      state.lanes.filter(l => l.metaData.matched).length <= state.offset
    ) {
      state.offset = 0;
    }
  },
  requestSaveLaneEdits: state => {
    state.saveLaneEditsLoading = true;
  },
  saveLaneEditsFailure: state => {
    state.saveLaneEditsFailure = true;
    state.saveLaneEditsLoading = false;
  },
  saveLaneEditsReset: state => {
    state.saveLaneEditsFailure = false;
    state.saveLaneEditsLoading = false;
  },
  resetPricingGridFilters: (state, initialState) => {
    state.filtersApplied = false;
    state.columnFilters = initialState.columnFilters;
    state.rangeFilters = initialState.rangeFilters;
    state.columnFilterMenu = initialState.columnFilterMenu;
    state.searchTerm = "";
    if (state.lanes?.length > 0) {
      state.lanes.forEach(l => (l.metaData.matched = true));
      state.totalFiltered = state.lanes.length;
    }
  },
  toggleDisableLane: ({ lanes }, { payload }) => {
    payload.forEach(({ truckloadLaneId, disabled }) => {
      lanes.find(l => l.truckloadLaneId === truckloadLaneId).metaData.disabled =
        disabled;
    });
  },
  replaceDisabledLanes: (state, { payload }) => {
    const updatedLanes = payload.updatedLanes;
    state.lanes?.forEach((o, index) => {
      const updatedLane = updatedLanes?.find(
        ({ truckloadLaneId }) => truckloadLaneId === o.truckloadLaneId
      );
      if (updatedLane) {
        const metaData = o.metaData;
        state.lanes[index] = {
          ...updatedLane,
          isFetchingLaneCount: o.isFetchingLaneCount,
          isErrorLaneCount: o.isErrorLaneCount,
          hasMatches: o.hasMatches,
          metaData: { ...metaData, originaLane: updatedLane, disabled: false }
        };
      }
    });
  }
};

export default pricingSliceHelper;
